import { QueryClient } from 'react-query';
import dayjs from 'dayjs';
import moment from 'moment';
import {
  DigitiseFormsDetails,
  GetFormsResponse,
} from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { AutoRulesService } from '@weave/schema-gen-ts/dist/schemas/messaging/auto-rules/v1/service.pb';
import { CallRouteService } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { TenantAPI } from '@weave/schema-gen-ts/dist/schemas/phone/tenant/tenant_api.pb';
import { InsuranceVerificationAdapterService } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import { GetSalesforceBundleNameResponse } from '@weave/schema-gen-ts/dist/schemas/package-service/v1/package.pb';

import { CustomAxios, getResponseData } from 'redux/axios';
import { authorizedFetch } from 'helpers/utils';
import { getLocationRegistrationStatusDetails } from 'apis/tendlc/api';
import { portingSchemaApi } from 'apis/porting/porting.api';
import { usersApi } from 'redux/actions/users/users.api';
import { autoMsgDeliveryReportsApi } from 'redux/actions/auto-msg-queue/auto-msg-delivery-reports.action';
import { fetchStripeReaders } from 'components/payments/queries/payments-terminal.query';
import { MESSAGES_URL } from 'redux/actions/sms/api';
import { DeviceModel } from 'apis/devices';
import { AppointmentWritebackModel } from 'models/appointment.model';
import { MerchantModel } from 'models/merchant.model';
import { SmsModel } from 'models/sms-history.model';
import { StatusMapping } from 'models/writebacks.model';
import CallRecordModel from 'components/history-list-container/call-records-container/call-record.model';
import { Settings as ReviewsSettings } from 'components/settings/reviews/helpers';
import { LocationHealth } from 'components/sync-app/sync-app.types';
import { DependencyDataKeys } from '../../handoff-metrics/handoff-metrics.types';
import { OnlineSchedulingHealthType } from '../../handoff-metrics/online-scheduling.typses';
import { SafeQueryResult, UnifyHandoffQueryKeys } from './unify-handoff.queries';
import { UnifyMetricEvalContext } from '../unify-metrics.types';

const DEFAULT_STALE_TIME = 5 * 60 * 1000; // 5 minutes
const DEFAULT_CACHE_TIME = 10 * 60 * 1000; // 10 minutes

export const fetchPhoneTenantId = async (
  queryClient: QueryClient,
  orgId?: string,
  locationId?: string
): Promise<string> => {
  if (!orgId) return '';
  try {
    const tenantsResp = await queryClient.fetchQuery({
      queryKey: UnifyHandoffQueryKeys.item(orgId, 'phone-tenants'),
      queryFn: () => TenantAPI.GetTenants(authorizedFetch(), { orgId }),
      staleTime: 30 * 60 * 1000,
      cacheTime: DEFAULT_CACHE_TIME,
    });
    const tenants = tenantsResp?.tenants ?? [];
    if (locationId) {
      const matchingTenant = tenants.find((t) =>
        t.locations?.some((loc) => loc.id === locationId)
      );
      return matchingTenant?.id ?? '';
    }
    return tenants[0]?.id ?? '';
  } catch {
    return '';
  }
};

export const fetchCallRoutes = async (
  queryClient: QueryClient,
  phoneTenantId?: string
): Promise<{ callRouteId: string }[]> => {
  if (!phoneTenantId) return [];
  try {
    const routesResp = await queryClient.fetchQuery({
      queryKey: UnifyHandoffQueryKeys.item(phoneTenantId, 'call-routes'),
      queryFn: () =>
        CallRouteService.List(authorizedFetch(), { tenantId: phoneTenantId }),
      staleTime: DEFAULT_STALE_TIME,
      cacheTime: DEFAULT_CACHE_TIME,
    });
    return routesResp?.callRoutes || [];
  } catch {
    return [];
  }
};

export type DependencyFetcher<T = any> = (ctx: UnifyMetricEvalContext) => Promise<T>;

export const fetchCallLogs: DependencyFetcher<CallRecordModel[]> = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(ctx.locationId, DependencyDataKeys.CallLogs),
    queryFn: async () => {
      const resp = await CustomAxios.get<{ data: CallRecordModel[] }>(
        '/support/v1/calllogs',
        {
          headers: { 'Location-Id': ctx.locationId },
          params: {
            start: moment().subtract(1, 'week').startOf('day').toISOString(),
            end: moment().endOf('day').toISOString(),
          },
        }
      );
      return resp.data.data || [];
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchPhoneDevices: DependencyFetcher<DeviceModel[]> = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(ctx.locationId, DependencyDataKeys.PhoneDevices),
    queryFn: async () => {
      const response = await CustomAxios.get<{ data: DeviceModel[] }>(
        '/support/v1/phone/devices',
        { headers: { 'Location-Id': ctx.locationId } }
      );
      return response.data.data || [];
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchPhoneHours: DependencyFetcher = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(ctx.locationId, DependencyDataKeys.PhoneHours),
    queryFn: async () => {
      const phoneTenantId = await fetchPhoneTenantId(
        ctx.queryClient,
        ctx.orgId,
        ctx.locationId
      );
      if (!phoneTenantId) return {};
      return await CallRouteService.ListPhoneHours(authorizedFetch(), {
        tenantId: phoneTenantId,
      });
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchCallRoutesList: DependencyFetcher = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.CallRoutesList
    ),
    queryFn: async () => {
      const phoneTenantId = await fetchPhoneTenantId(
        ctx.queryClient,
        ctx.orgId,
        ctx.locationId
      );
      const callRoutes = await fetchCallRoutes(ctx.queryClient, phoneTenantId);
      if (!callRoutes.length) return [];
      return await Promise.all(
        callRoutes.map((cr) =>
          CallRouteService.Read(authorizedFetch(), { callRouteId: cr.callRouteId })
        )
      );
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchSms: DependencyFetcher<SmsModel[]> = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(ctx.locationId, DependencyDataKeys.Sms),
    queryFn: async () => {
      const responses = await CustomAxios.get<{ smsList: SmsModel[] }>(MESSAGES_URL, {
        headers: { 'Location-Id': ctx.locationId },
        params: {
          limit: 200,
          start: dayjs().subtract(7, 'day').toISOString(),
          end: new Date().toISOString(),
          location_id: ctx.locationId,
        },
      });
      return responses.data?.smsList || [];
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchUsers: DependencyFetcher = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(ctx.locationId, DependencyDataKeys.Users),
    queryFn: async () => (await usersApi.getUsers(200, ctx.locationId)) || [],
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchMessagingRules: DependencyFetcher = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.MessagingRules
    ),
    queryFn: async () => {
      if (!ctx.orgId) return [];
      const resp = await AutoRulesService.ListRules(authorizedFetch(), {
        orgId: ctx.orgId,
        businessGroups: [ctx.locationId],
        skipFilterValidation: true,
        summaryOnly: true,
      });
      return resp.rules;
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchAutoMsgLocationSettings: DependencyFetcher = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.AutoMsgLocationSettings
    ),
    queryFn: async () => autoMsgDeliveryReportsApi.getLocationSettings(ctx.locationId),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchAppointmentWritebacks: DependencyFetcher<
  AppointmentWritebackModel[]
> = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.AppointmentWritebacks
    ),
    queryFn: async () => {
      const resp = await CustomAxios.get<{ data: AppointmentWritebackModel[] }>(
        '/support/v1/appointments',
        {
          headers: { 'Location-Id': ctx.locationId },
          params: {
            start: moment().subtract(1, 'week').format('YYYY-MM-DD'),
            end: moment().add(1, 'day').format('YYYY-MM-DD'),
          },
        }
      );
      return resp.data?.data || [];
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchWritebacksStatusMappings: DependencyFetcher<StatusMapping[]> = async (
  ctx
) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.WritebacksStatusMappings
    ),
    queryFn: async () => {
      const targetLocId = ctx.parentLocationId || ctx.locationId;
      const resp = await CustomAxios.get<{ data: StatusMapping[] }>(
        'support/v1/notifications/status-mappings/writeback',
        { headers: { 'Location-Id': targetLocId } }
      );
      return resp.data?.data || [];
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchAppointmentsStatusMappings: DependencyFetcher<StatusMapping[]> = async (
  ctx
) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.AppointmentsStatusMappings
    ),
    queryFn: async () => {
      const resp = await CustomAxios.get<{ data: StatusMapping[] }>(
        'support/v1/notifications/status-mappings/appointment',
        { headers: { 'Location-Id': ctx.locationId } }
      );
      return resp.data?.data || [];
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchSyncappHealth: DependencyFetcher<LocationHealth> = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.SyncappHealth
    ),
    queryFn: async () => {
      const targetLocId = ctx.parentLocationId || ctx.locationId;
      const resp = await CustomAxios.get<{ data: LocationHealth }>(
        'support/v1/syncapp/health',
        { headers: { 'Location-Id': targetLocId } }
      );
      return resp?.data?.data ?? ({} as LocationHealth);
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchPortingData: DependencyFetcher = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(ctx.locationId, DependencyDataKeys.PortingData),
    queryFn: async () => portingSchemaApi.getPortingDataByLocationIds([ctx.locationId]),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchReviewsSettings: DependencyFetcher<ReviewsSettings> = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.ReviewsSettings
    ),
    queryFn: async () => {
      const resp = await CustomAxios.get<{ data: ReviewsSettings }>(
        'support/v1/reviews/settings',
        { headers: { 'Location-Id': ctx.locationId } }
      );
      return resp?.data?.data ?? ({} as ReviewsSettings);
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchFaxHistory: DependencyFetcher = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(ctx.locationId, DependencyDataKeys.FaxHistory),
    queryFn: async () => {
      const params = {
        start: dayjs().subtract(7, 'day').toISOString(),
        end: new Date().toISOString(),
        fromdid: '',
        todid: '',
        limit: '26',
        skip: '',
        order: 'asc',
      };
      const buildQS = (p: typeof params) =>
        Object.entries(p).reduce((str, [k, v], idx) => {
          if (!v.trim()) return str;
          return `${str}${idx > 0 ? '&' : ''}${k}=${v.trim()}`;
        }, '');
      const data = await CustomAxios.get(`/support/v1/fax?${buildQS(params)}`, {
        headers: { 'Location-Id': ctx.locationId },
      }).then(getResponseData);
      return data ?? [];
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchFaxProviderSettings: DependencyFetcher = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.FaxProviderSettings
    ),
    queryFn: async () => {
      const faxNumber = await CustomAxios.get(`/support/v1/fax/did`, {
        headers: { 'Location-Id': ctx.locationId },
      }).then(getResponseData);
      if (faxNumber?.DID && faxNumber.DID !== '') {
        const response = await CustomAxios.get(
          `support/v1/fax/providerdidinfo/${faxNumber['DID']}`,
          { headers: { 'Location-Id': ctx.locationId } }
        );
        return response;
      }
      return undefined;
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchDigitalForms: DependencyFetcher = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(ctx.locationId, DependencyDataKeys.DigitalForms),
    queryFn: async () => {
      const response = await CustomAxios.get<GetFormsResponse>(
        `/forms-digital/v2/forms?company_ids=${ctx.locationId}&pageRequest.pageLimit=100&pageRequest.pageNumber=1&searchKey=`,
        { headers: { 'Location-Id': ctx.locationId } }
      );
      return response?.data?.data ?? [];
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchSubmittedDigitizationForms: DependencyFetcher = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.SubmittedDigitizationForms
    ),
    queryFn: async () => {
      const resp = await CustomAxios.get<DigitiseFormsDetails>(
        `/forms/v1/digitization?companyId=${ctx.locationId}`,
        { headers: { 'Location-Id': ctx.locationId } }
      );
      return resp?.data?.data ?? [];
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchOnlineSchedulingHealth: DependencyFetcher<
  OnlineSchedulingHealthType | undefined
> = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.OnlineSchedulingHealth
    ),
    queryFn: async () => {
      const response = await CustomAxios.get<{ data: OnlineSchedulingHealthType }>(
        `schedule/api/v2/health`,
        { headers: { 'Location-Id': ctx.locationId } }
      );
      return response?.data?.data;
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchCustomizationFlags: DependencyFetcher = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.CustomizationFlags
    ),
    queryFn: async () => {
      if (ctx?.features) {
        return { features: ctx.features };
      }
      return await CustomAxios.get(
        `platform-location-feature/v1/location/${ctx.locationId}/features`,
        { headers: { 'Location-Id': ctx.locationId } }
      );
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchLocationTasks: DependencyFetcher = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.LocationTasks
    ),
    queryFn: async () =>
      CustomAxios.get('insys/onboarding/v1/onboarding-tasks/modules-tasks', {
        headers: { 'Location-Id': ctx.locationId },
      }).then(getResponseData),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchPaymentsSettings: DependencyFetcher = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.PaymentsSettings
    ),
    queryFn: async () => {
      const resp = await CustomAxios.get<{ data: MerchantModel }>(
        `payments/v1/merchant`,
        { headers: { 'Location-Id': ctx.locationId } }
      );
      const stripeReaders = await fetchStripeReaders({
        paymentsUrl: ctx.paymentsUrl,
        stripeLocationId: resp.data?.data?.terminalLocation,
      });
      return {
        merchantData: resp.data?.data,
        stripeReaders,
      };
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchSalesforceBundleSettings: DependencyFetcher = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.SalesforceBundleSettings
    ),
    queryFn: async () => {
      if (ctx?.salesforceBundle) {
        return ctx.salesforceBundle;
      }
      const resp = await CustomAxios.get<GetSalesforceBundleNameResponse>(
        `/package-service/v1/salesforce-bundle-name`,
        { headers: { 'Location-Id': ctx.locationId } }
      );
      return resp?.data;
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchTenDlcLocationRegistrationStatus: DependencyFetcher = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.tenDlcLocationRegistrationStatus
    ),
    queryFn: async () =>
      getLocationRegistrationStatusDetails({ locationId: ctx.locationId }),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const fetchWiePayerConnectionStatus: DependencyFetcher = async (ctx) =>
  ctx.queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(
      ctx.locationId,
      DependencyDataKeys.WIEPayerConnectionStatus
    ),
    queryFn: async () =>
      InsuranceVerificationAdapterService.GetPayerConnectionDetailsByLocationId(
        authorizedFetch(),
        { locationId: ctx.locationId }
      ),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });

export const dependencyFetchers: Partial<Record<DependencyDataKeys, DependencyFetcher>> =
  {
    [DependencyDataKeys.CallLogs]: fetchCallLogs,
    [DependencyDataKeys.PhoneDevices]: fetchPhoneDevices,
    [DependencyDataKeys.PhoneHours]: fetchPhoneHours,
    [DependencyDataKeys.CallRoutesList]: fetchCallRoutesList,
    [DependencyDataKeys.Sms]: fetchSms,
    [DependencyDataKeys.Users]: fetchUsers,
    [DependencyDataKeys.MessagingRules]: fetchMessagingRules,
    [DependencyDataKeys.AutoMsgLocationSettings]: fetchAutoMsgLocationSettings,
    [DependencyDataKeys.AppointmentWritebacks]: fetchAppointmentWritebacks,
    [DependencyDataKeys.WritebacksStatusMappings]: fetchWritebacksStatusMappings,
    [DependencyDataKeys.AppointmentsStatusMappings]: fetchAppointmentsStatusMappings,
    [DependencyDataKeys.SyncappHealth]: fetchSyncappHealth,
    [DependencyDataKeys.PortingData]: fetchPortingData,
    [DependencyDataKeys.ReviewsSettings]: fetchReviewsSettings,
    [DependencyDataKeys.FaxHistory]: fetchFaxHistory,
    [DependencyDataKeys.FaxProviderSettings]: fetchFaxProviderSettings,
    [DependencyDataKeys.DigitalForms]: fetchDigitalForms,
    [DependencyDataKeys.SubmittedDigitizationForms]: fetchSubmittedDigitizationForms,
    [DependencyDataKeys.OnlineSchedulingHealth]: fetchOnlineSchedulingHealth,
    [DependencyDataKeys.LocationTasks]: fetchLocationTasks,
    [DependencyDataKeys.CustomizationFlags]: fetchCustomizationFlags,
    [DependencyDataKeys.SalesforceBundleSettings]: fetchSalesforceBundleSettings,
    [DependencyDataKeys.PaymentsSettings]: fetchPaymentsSettings,
    [DependencyDataKeys.tenDlcLocationRegistrationStatus]:
      fetchTenDlcLocationRegistrationStatus,
    [DependencyDataKeys.WIEPayerConnectionStatus]: fetchWiePayerConnectionStatus,
  };

export const fetchDependencyData = async (
  ctx: UnifyMetricEvalContext,
  key: DependencyDataKeys
): Promise<SafeQueryResult<any>> => {
  const fetcher = dependencyFetchers[key];
  if (!fetcher) {
    return {
      data: undefined,
      error: null,
      isLoading: false,
      isError: false,
      isSuccess: true,
    };
  }

  try {
    const data = await fetcher(ctx);
    return {
      data,
      error: null,
      isLoading: false,
      isError: false,
      isSuccess: true,
    };
  } catch (err: unknown) {
    return {
      data: undefined,
      error: err instanceof Error ? err : new Error(String(err)),
      isLoading: false,
      isError: true,
      isSuccess: false,
    };
  }
};

export const fetchMultipleDependencyData = async (
  ctx: UnifyMetricEvalContext,
  keys: DependencyDataKeys[]
): Promise<Record<string, SafeQueryResult<any>>> => {
  const entries = await Promise.all(
    keys.map(async (key) => [key, await fetchDependencyData(ctx, key)] as const)
  );
  return Object.fromEntries(entries);
};
